/**
 * ExplainabilityPanel — SHAP explanations, feature importance charts
 * Phase 4.8-B — WQ-198
 */
import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  computeExplanation, getExplanation, computeBatchExplanation,
  type Explanation, type BatchExplanation, type FeatureContribution,
} from '../../services/explainabilityService';
import { fmt } from '../../utils/formatNumber';

const POSITIVE_COLOR = '#22c55e';
const NEGATIVE_COLOR = '#dc2626';

const FeatureImportanceChart: React.FC<{ features: FeatureContribution[] }> = ({ features }) => {
  const sorted = [...features].sort((a, b) => b.abs_contribution - a.abs_contribution).slice(0, 15);
  const maxAbs = Math.max(...sorted.map(f => f.abs_contribution), 0.001);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {sorted.map((f, i) => {
        const widthPct = (f.abs_contribution / maxAbs) * 100;
        const isPositive = f.contribution >= 0;
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 140, textAlign: 'right', fontSize: 12, fontWeight: 500, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {f.feature}
            </div>
            <div style={{ flex: 1, height: 18, position: 'relative', background: 'var(--bg-secondary)', borderRadius: 3 }}>
              <div style={{
                position: 'absolute', top: 0, bottom: 0, left: 0,
                width: `${widthPct}%`, borderRadius: 3,
                background: isPositive ? POSITIVE_COLOR : NEGATIVE_COLOR,
                opacity: 0.7,
              }} />
            </div>
            <div style={{ width: 70, textAlign: 'right', fontSize: 11, fontFamily: 'monospace', color: isPositive ? POSITIVE_COLOR : NEGATIVE_COLOR }}>
              {isPositive ? '+' : ''}{fmt(f.contribution, 4)}
            </div>
            <div style={{ width: 60, textAlign: 'right', fontSize: 11, color: 'var(--text-secondary)' }}>
              {f.value !== null && f.value !== undefined ? String(f.value) : '—'}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ExplainabilityPanel: React.FC = () => {
  const { authFetch } = useAuth();
  const [activeView, setActiveView] = useState<'single' | 'batch'>('single');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Single explanation
  const [predictionId, setPredictionId] = useState('');
  const [explanation, setExplanation] = useState<Explanation | null>(null);

  // Batch explanation
  const [batchIds, setBatchIds] = useState('');
  const [batchExplanation, setBatchExplanation] = useState<BatchExplanation | null>(null);

  // Load existing
  const [loadId, setLoadId] = useState('');

  const handleCompute = async () => {
    if (!predictionId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await computeExplanation(authFetch, { prediction_id: predictionId });
      setExplanation(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Explanation failed');
    }
    setLoading(false);
  };

  const handleLoad = async () => {
    if (!loadId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await getExplanation(authFetch, loadId);
      setExplanation(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load explanation');
    }
    setLoading(false);
  };

  const handleBatchCompute = async () => {
    const ids = batchIds.split(',').map(s => s.trim()).filter(Boolean);
    if (ids.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      const result = await computeBatchExplanation(authFetch, { prediction_ids: ids });
      setBatchExplanation(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Batch explanation failed');
    }
    setLoading(false);
  };

  return (
    <div>
      {/* View toggle */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {([
          { key: 'single', label: 'Single Prediction' },
          { key: 'batch', label: 'Batch Analysis' },
        ] as const).map(v => (
          <button key={v.key} onClick={() => setActiveView(v.key)} style={{
            fontSize: 13, padding: '6px 16px', borderRadius: 6,
            border: '1px solid var(--border)', cursor: 'pointer', fontWeight: 500,
            background: activeView === v.key ? 'var(--accent-primary)' : 'var(--bg-secondary)',
            color: activeView === v.key ? '#fff' : 'var(--text-primary)',
          }}>
            {v.label}
          </button>
        ))}
      </div>

      {error && <div style={{ padding: 12, background: '#fef2f2', color: '#dc2626', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

      {/* Single prediction view */}
      {activeView === 'single' && (
        <div>
          <div style={{
            padding: 16, borderRadius: 8, border: '1px solid var(--border)',
            background: 'var(--bg-secondary)', marginBottom: 16,
          }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Prediction ID</label>
                <input
                  value={predictionId} onChange={e => setPredictionId(e.target.value)}
                  placeholder="Enter prediction ID to explain"
                  style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: 13 }}
                />
              </div>
              <button onClick={handleCompute} disabled={loading || !predictionId} style={{
                fontSize: 13, padding: '6px 20px', borderRadius: 6, border: 'none',
                fontWeight: 600, cursor: 'pointer', background: '#22c55e', color: '#fff',
                opacity: (!predictionId || loading) ? 0.5 : 1,
              }}>
                {loading ? 'Computing...' : 'Compute SHAP'}
              </button>
              <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end' }}>
                <input
                  value={loadId} onChange={e => setLoadId(e.target.value)}
                  placeholder="Explanation ID"
                  style={{ width: 160, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: 13 }}
                />
                <button onClick={handleLoad} disabled={loading || !loadId} style={{
                  fontSize: 13, padding: '6px 12px', borderRadius: 6,
                  border: '1px solid var(--border)', fontWeight: 500, cursor: 'pointer',
                  background: 'var(--bg-primary)', color: 'var(--text-primary)',
                }}>
                  Load
                </button>
              </div>
            </div>
          </div>

          {explanation && (
            <div style={{ padding: 16, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>Feature Importance (SHAP)</div>
                <div style={{ display: 'flex', gap: 12, fontSize: 12, color: 'var(--text-secondary)' }}>
                  {explanation.base_value !== null && <span>Base: {explanation.base_value.toFixed(4)}</span>}
                  {explanation.output_value !== null && <span>Output: {explanation.output_value.toFixed(4)}</span>}
                  <span>Method: {explanation.method}</span>
                </div>
              </div>
              <FeatureImportanceChart features={explanation.feature_importance} />
            </div>
          )}

          {!explanation && !loading && (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
              Enter a prediction ID and click "Compute SHAP" to generate an explanation.
            </div>
          )}
        </div>
      )}

      {/* Batch view */}
      {activeView === 'batch' && (
        <div>
          <div style={{
            padding: 16, borderRadius: 8, border: '1px solid var(--border)',
            background: 'var(--bg-secondary)', marginBottom: 16,
          }}>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
              Prediction IDs (comma-separated)
            </label>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
              <input
                value={batchIds} onChange={e => setBatchIds(e.target.value)}
                placeholder="id1, id2, id3..."
                style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: 13 }}
              />
              <button onClick={handleBatchCompute} disabled={loading || !batchIds.trim()} style={{
                fontSize: 13, padding: '6px 20px', borderRadius: 6, border: 'none',
                fontWeight: 600, cursor: 'pointer', background: '#22c55e', color: '#fff',
                opacity: (!batchIds.trim() || loading) ? 0.5 : 1,
              }}>
                {loading ? 'Computing...' : 'Compute Batch'}
              </button>
            </div>
          </div>

          {batchExplanation && (
            <div>
              {/* Aggregate importance */}
              {batchExplanation.aggregate_importance && batchExplanation.aggregate_importance.length > 0 && (
                <div style={{ padding: 16, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)', marginBottom: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Aggregate Feature Importance</div>
                  <FeatureImportanceChart features={batchExplanation.aggregate_importance} />
                </div>
              )}

              {/* Individual explanations */}
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                Individual Explanations ({batchExplanation.explanations.length})
              </div>
              {batchExplanation.explanations.map((exp, i) => (
                <div key={i} style={{ padding: 12, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)', marginBottom: 8 }}>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
                    Prediction: {exp.prediction_id}
                    {exp.output_value !== null && ` | Output: ${exp.output_value.toFixed(4)}`}
                  </div>
                  <FeatureImportanceChart features={exp.feature_importance} />
                </div>
              ))}
            </div>
          )}

          {!batchExplanation && !loading && (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
              Enter prediction IDs to compute batch SHAP explanations.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExplainabilityPanel;
